import React, { useCallback, useEffect, useState } from 'react';
import loadable from '@loadable/component';
import { reportError } from '../../lib/reportError';
import {
  authenticate,
  AuthAction,
  AuthErrorMessage,
  AuthAttributes,
  AuthPayload
} from '../../lib/authenticate';
import { loadAmazonLoginScript, loadFacebookLoginScript } from '../../lib/loadScript';
import { loginOnAmazon } from '../../lib/amazon';
import { loginOnFacebook } from '../../lib/facebook';
import { useCookies } from '../useCookies';
import { useUser } from '../useUser';

const ConfirmSocialAccount = loadable(
  () => import('../../components/Authentication/ConfirmSocialAccount')
);
const LinkSocialAccount = loadable(
  () => import('../../components/Authentication/LinkSocialAccount')
);
const SocialAuthModalOverlay = loadable(
  () => import('../../components/Authentication/SocialAuthModalOverlay')
);

export enum SocialAuthProvider {
  Amazon = 'AMAZON',
  Facebook = 'FACEBOOK'
}

export enum SocialAuthStep {
  Login,
  ConfirmAccount,
  LinkAccounts
}

const ERROR_MESSAGES = {
  AMAZON: 'Amazon authorization failed!',
  FACEBOOK: 'Facebook authorization failed!'
};

export type ConfirmAccountAttributes = {
  remember: boolean;
  email: string;
  first_name: string;
  last_name: string;
};

export type LinkAccountsAttributes = {
  remember: boolean;
  email: string;
  password: string;
};

export const authenticateAmazon = async (
  action: AuthAction,
  attributes?: Partial<AuthAttributes>
) => {
  const loginResponse = await loginOnAmazon();

  return await authenticate({
    action,
    attributes: {
      access_token: loginResponse.access_token,
      provider: SocialAuthProvider.Amazon.toLowerCase(),
      ...attributes
    }
  });
};

export const authenticateFacebook = async (
  action: AuthAction,
  attributes?: Partial<AuthAttributes>
) => {
  const accessToken = await loginOnFacebook();

  return await authenticate({
    action,
    attributes: {
      access_token: accessToken,
      provider: SocialAuthProvider.Facebook.toLowerCase(),
      ...attributes
    }
  });
};

const HANDLERS = {
  [SocialAuthProvider.Facebook]: authenticateFacebook,
  [SocialAuthProvider.Amazon]: authenticateAmazon
};

export const useSocialAuth = (
  provider: SocialAuthProvider,
  onAuth?: (user: AuthPayload) => void
) => {
  const [step, setStep] = useState(SocialAuthStep.Login);
  const [error, setError] = useState<AuthErrorMessage>('');
  const { setUserData } = useUser();
  const {
    cookies: { cartId }
  } = useCookies();

  const authenticate = HANDLERS[provider];

  const handleLogin = useCallback(() => {
    return authenticate(AuthAction.SignIn, { cart_id: cartId })
      .then(user => {
        setUserData({ remember: true, isLoggedIn: true, ...user }).then(() => onAuth?.(user));
      })
      .catch(error => {
        if (error.code === 'confirm_account') {
          setStep(SocialAuthStep.ConfirmAccount);
        } else if (error.code === 'connect_accounts') {
          setStep(SocialAuthStep.LinkAccounts);
        } else {
          reportError(error);
          setError(ERROR_MESSAGES[provider]);
        }
      });
  }, [authenticate, setUserData, provider, onAuth, cartId]);

  const handleConfirmAccount = useCallback(
    ({ remember, ...rest }: ConfirmAccountAttributes) =>
      authenticate(AuthAction.Register, { cart_id: cartId, ...rest }).then(user => {
        setUserData({ remember, isLoggedIn: true, ...user }).then(() => onAuth?.(user));
      }),
    [authenticate, setUserData, onAuth, cartId]
  );

  const handleLinkAccounts = useCallback(
    ({ remember, ...rest }: LinkAccountsAttributes) =>
      authenticate(AuthAction.SignIn, { link_accounts: true, cart_id: cartId, ...rest }).then(
        user => {
          setUserData({ remember, isLoggedIn: true, ...user }).then(() => onAuth?.(user));
        }
      ),
    [authenticate, setUserData, onAuth, cartId]
  );

  const renderModals = useCallback(
    () => (
      <>
        <SocialAuthModalOverlay
          onClose={() => setStep(SocialAuthStep.Login)}
          show={step === SocialAuthStep.ConfirmAccount}
        >
          <ConfirmSocialAccount
            provider={provider}
            onSubmit={handleConfirmAccount}
            onSignInClick={() => setStep(SocialAuthStep.LinkAccounts)}
          />
        </SocialAuthModalOverlay>

        <SocialAuthModalOverlay
          onClose={() => setStep(SocialAuthStep.Login)}
          show={step === SocialAuthStep.LinkAccounts}
        >
          <LinkSocialAccount
            provider={provider}
            onSubmit={handleLinkAccounts}
            onChangeEmailClick={() => setStep(SocialAuthStep.ConfirmAccount)}
          />
        </SocialAuthModalOverlay>
      </>
    ),
    [handleConfirmAccount, handleLinkAccounts, provider, step]
  );

  useEffect(() => {
    switch (provider) {
      case SocialAuthProvider.Amazon:
        return loadAmazonLoginScript();
      case SocialAuthProvider.Facebook:
        return loadFacebookLoginScript();
    }
  }, [provider]);

  return {
    step,
    setStep,
    error,
    handleLogin,
    handleConfirmAccount,
    handleLinkAccounts,
    renderModals
  };
};
